// src/pages/Kontakt.tsx
import { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Mail, Phone, Instagram, Facebook, Home, MapPin } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';

// Define the form data type
interface FormData {
  ime: string;
  priimek: string;
  email: string;
  telefon: string;
  sporocilo: string;
  gdprConsent: boolean;
}

// Define the errors type
interface FormErrors {
  ime: boolean;
  priimek: boolean;
  email: boolean;
  gdprConsent: boolean;
}

const Kontakt = () => {
  const [formData, setFormData] = useState<FormData>({
    ime: '',
    priimek: '',
    email: '',
    telefon: '',
    sporocilo: '',
    gdprConsent: false,
  });

  const [errors, setErrors] = useState<FormErrors>({
    ime: false,
    priimek: false,
    email: false,
    gdprConsent: false,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name as keyof FormErrors]: false }));
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, gdprConsent: checked }));
    if (errors.gdprConsent) {
      setErrors((prev) => ({ ...prev, gdprConsent: false }));
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Simple validation
    const newErrors: FormErrors = {
      ime: !formData.ime.trim(),
      priimek: !formData.priimek.trim(),
      email: !formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
      gdprConsent: !formData.gdprConsent,
    };

    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
      return;
    }

    // In a real app, we would send the data to a server
    console.log('Form data submitted:', formData);

    // Show success toast
    toast({
      title: 'Sporočilo poslano',
      description:
        'Hvala za vaše sporočilo. Odgovorili vam bomo v najkrajšem možnem času.',
    });

    // Reset form
    setFormData({
      ime: '',
      priimek: '',
      email: '',
      telefon: '',
      sporocilo: '',
      gdprConsent: false,
    });
  };

  return (
    <Layout
      title="Kontakt | Lux Decor by Kamen"
      description="Stopite v stik z nami za več informacij o naši luksuzni kopalniški opremi ali za osebno svetovanje."
    >
      <section className="bg-luxbeige py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-semibold mb-2">Stopite v stik z nami</h1>
            <p className="text-luxgray max-w-2xl mx-auto">
              Za vse informacije smo vam z veseljem na voljo. Izpolnite spodnji obrazec ali nas kontaktirajte neposredno.
            </p>
            <div className="h-px w-24 bg-luxgold mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="lux-card">
              <h2 className="text-2xl font-semibold mb-6">Kontaktni podatki</h2>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-luxgold mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Proizvodnja</h3>
                    <p className="text-luxgray">Levarska ulica 3, 2314 Zgornja Polskava, Slovenija</p>
                    <a
                      href="https://maps.google.com/?q=Levarska+ulica+3,+2314+Zgornja+Polskava,+Slovenija"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-luxgold hover:underline text-sm inline-flex items-center mt-1"
                    >
                      Odpri Google Maps
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-1"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Home className="w-6 h-6 text-luxgold mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Showroom</h3>
                    <p className="text-luxgray">Ptujska cesta 110, 2000 Maribor, Slovenija</p>
                    <a
                      href="https://maps.google.com/?q=Ptujska+cesta+110,+2000+Maribor,+Slovenija"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-luxgold hover:underline text-sm inline-flex items-center mt-1"
                    >
                      Odpri Google Maps
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-1"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Phone className="w-6 h-6 text-luxgold mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Telefon</h3>
                    <p>
                      <a href="tel:+38631467272" className="text-luxdark hover:text-luxgold transition-colors">
                        +386 31 467 272
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 text-luxgold mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">E-mail</h3>
                    <p>
                      <a href="mailto:info@luxdecor.si" className="text-luxdark hover:text-luxgold transition-colors">
                        info@luxdecor.si
                      </a>
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <h3 className="font-medium text-lg mb-2">Družbena omrežja</h3>
                  <div className="flex space-x-4">
                    <a
                      href="https://www.instagram.com/lux_decor_kamen/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-luxgold/10 p-3 rounded-full hover:bg-luxgold/20 transition-colors"
                      aria-label="Obiščite nas na Instagramu"
                    >
                      <Instagram className="w-5 h-5 text-luxgold" />
                    </a>
                    <a
                      href="https://www.facebook.com/profile.php?id=61567721153960"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-luxgold/10 p-3 rounded-full hover:bg-luxgold/20 transition-colors"
                      aria-label="Obiščite nas na Facebooku"
                    >
                      <Facebook className="w-5 h-5 text-luxgold" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="lux-card">
              <h2 className="text-2xl font-semibold mb-6">Pošljite nam sporočilo</h2>

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ime" className="block text-sm font-medium mb-1">
                      Ime
                    </label>
                    <input
                      type="text"
                      id="ime"
                      name="ime"
                      value={formData.ime}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border ${
                        errors.ime ? 'border-red-500' : 'border-luxgold/20'
                      } rounded focus:outline-none focus:ring-2 focus:ring-luxgold/50`}
                    />
                    {errors.ime && <p className="text-red-500 text-xs mt-1">Prosimo, vnesite ime</p>}
                  </div>
                  <div>
                    <label htmlFor="priimek" className="block text-sm font-medium mb-1">
                      Priimek
                    </label>
                    <input
                      type="text"
                      id="priimek"
                      name="priimek"
                      value={formData.priimek}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border ${
                        errors.priimek ? 'border-red-500' : 'border-luxgold/20'
                      } rounded focus:outline-none focus:ring-2 focus:ring-luxgold/50`}
                    />
                    {errors.priimek && (
                      <p className="text-red-500 text-xs mt-1">Prosimo, vnesite priimek</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border ${
                      errors.email ? 'border-red-500' : 'border-luxgold/20'
                    } rounded focus:outline-none focus:ring-2 focus:ring-luxgold/50`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      Prosimo, vnesite veljaven e-mail naslov
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="telefon" className="block text-sm font-medium mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    value={formData.telefon}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-luxgold/20 rounded focus:outline-none focus:ring-2 focus:ring-luxgold/50"
                  />
                </div>

                <div>
                  <label htmlFor="sporocilo" className="block text-sm font-medium mb-1">
                    Sporočilo
                  </label>
                  <textarea
                    id="sporocilo"
                    name="sporocilo"
                    rows={4}
                    value={formData.sporocilo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-luxgold/20 rounded focus:outline-none focus:ring-2 focus:ring-luxgold/50"
                  ></textarea>
                </div>

                <div className="flex items-start space-x-2 py-2">
                  <Checkbox
                    id="gdprConsent"
                    checked={formData.gdprConsent}
                    onCheckedChange={handleCheckboxChange}
                    className={`mt-1 ${errors.gdprConsent ? 'border-red-500' : ''}`}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="gdprConsent"
                      className={`text-sm font-medium leading-none ${
                        errors.gdprConsent ? 'text-red-500' : 'text-gray-700'
                      }`}
                    >
                      Z oddajo obrazca soglašam, da KAMEN d.o.o. kot upravljavec osebnih podatkov obdeluje moje osebne podatke, ki sem jih navedel/-a v tem obrazcu, za namen priprave in posredovanja ponudbe ter morebitne nadaljnje komunikacije v zvezi z mojo zahtevo. *
                    </label>
                    {errors.gdprConsent && (
                      <p className="text-red-500 text-xs">Za nadaljevanje je potrebno soglasje</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Za več informacij si oglejte našo{' '}
                      <Link to="/gdpr" className="text-luxgold hover:underline">
                        GDPR politiko
                      </Link>
                      .
                    </p>
                  </div>
                </div>

                <Button type="submit" className="w-full bg-luxgold hover:bg-luxgold/90 text-white">
                  Pošlji sporočilo
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Kontakt;